import { Component, OnDestroy, OnInit } from "@angular/core";
import * as moment from "moment";
import { Subscription } from "rxjs";
import { NotifierService } from "src/app/_services/notifier.service";
import { APIService } from "src/app/api/api.service";
import { environment } from "src/environments/environment";

type SeverityFilter = "all" | "high" | "medium" | "low";
type AlertColumnKey =
  | "eventTime"
  | "criticality"
  | "description"
  | "identifier"
  | "triggeredBy"
  | "ruleId"
  | "ruleName"
  | "protocol"
  | "sourceIp"
  | "fromToIp";

interface AzureAlertApiItem {
  event_time?: string;
  criticality?: string;
  description?: string;
  identifier?: string;
  triggered_by?: string;
  rule_id?: string;
  rule_name?: string;
  protocol?: string;
  source_ip?: string;
  from_ip?: string;
  to_ip?: string;
}

interface AzureAlertViewModel {
  eventTime: string;
  eventTimestamp: number;
  criticality: string;
  severityBucket: SeverityFilter;
  description: string;
  identifier: string;
  triggeredBy: string;
  ruleId: string;
  ruleName: string;
  protocol: string;
  sourceIp: string;
  fromToIp: string;
}

interface AlertColumn {
  key: AlertColumnKey;
  label: string;
}

@Component({
  selector: "app-azure-tell-me-alerts",
  templateUrl: "./azure-tell-me-alerts.component.html",
  styleUrls: ["./azure-tell-me-alerts.component.css"],
})
export class AzureTellMeAlertsComponent implements OnInit, OnDestroy {
  userId: string = localStorage.getItem("selectedClientList");
  subscriptionId: string = localStorage.getItem("accountId");
  urlPrefix: string = localStorage.getItem("role") == "Admin" ? "admin" : "client";

  loading: boolean = false;
  errorMessage: string = null;

  categories: string[] = ["Security", "Governance", "Daily Spend", "Daily Cost", "Others"];
  activeCategory: string = "Security";

  searchKeyword: string = "";
  selectedSeverity: SeverityFilter = "all";
  showSeverityFilter: boolean = false;

  currentPage: number = 1;
  itemsPerPage: number = 10;

  summary: any = {
    total_alerts: 0,
    high_alerts: 0,
    medium_alerts: 0,
    low_alerts: 0,
  };

  alertsByCategory: Record<string, AzureAlertViewModel[]> = {};
  columnsByCategory: Record<string, AlertColumnKey[]> = {};
  private currentMessage: Subscription;
  private readonly columnLabels: Record<AlertColumnKey, string> = {
    eventTime: "Event Time",
    criticality: "Criticality",
    description: "Description",
    identifier: "Identifier",
    triggeredBy: "Triggered By",
    ruleId: "Rule ID",
    ruleName: "Rule Name",
    protocol: "Protocol",
    sourceIp: "Source IP",
    fromToIp: "From IP - To IP",
  };
  private readonly defaultColumnsByCategory: Record<string, AlertColumnKey[]> = {
    Security: [
      "eventTime",
      "criticality",
      "description",
      "identifier",
      "triggeredBy",
      "ruleId",
      "ruleName",
      "protocol",
      "sourceIp",
      "fromToIp",
    ],
    Governance: [
      "eventTime",
      "criticality",
      "description",
      "identifier",
      "triggeredBy",
      "ruleId",
      "ruleName",
    ],
    "Daily Spend": [
      "eventTime",
      "criticality",
      "description",
      "ruleId",
      "ruleName",
    ],
    "Daily Cost": [
      "eventTime",
      "criticality",
      "description",
      "ruleId",
      "ruleName",
    ],
    Others: ["eventTime", "criticality", "description", "triggeredBy"],
  };

  constructor(private apiService: APIService, private notifier: NotifierService) {
    this.categories.forEach((category: string) => {
      this.alertsByCategory[category] = [];
      this.columnsByCategory[category] = this.defaultColumnsByCategory[category] || [];
    });
  }

  ngOnInit(): void {
    this.currentMessage = this.notifier.currentMessage.subscribe((msg: any) => {
      if (!msg) {
        return;
      }

      let data: any = null;
      try {
        data = JSON.parse(msg);
      } catch {
        return;
      }

      if (data.source != "notifier" || data.value == null) {
        return;
      }

      if (data.key == "accountId") {
        this.subscriptionId = data.value;
        this.load();
      }
    });

    this.load();
  }

  ngOnDestroy(): void {
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
  }

  async load() {
    this.userId = localStorage.getItem("selectedClientList");
    this.subscriptionId = localStorage.getItem("accountId");
    this.urlPrefix = localStorage.getItem("role") == "Admin" ? "admin" : "client";

    if (!this.userId || !this.subscriptionId) {
      this.resetData();
      this.errorMessage = "Unable to load Azure alerts. Missing user or subscription context.";
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    this.resetData();

    this.notifier.loading(true);

    try {
      await Promise.all([
        this.fetchAlertsSummary(),
        ...this.categories.map((category: string) => this.fetchAlertsByCategory(category)),
      ]);
    } catch {
      this.errorMessage = "Unable to load Azure alerts. Please try again.";
      this.notifier.alert("Error", "", this.errorMessage, "error", 5000);
    } finally {
      this.loading = false;
      this.ensurePageBounds();
      this.notifier.loading(false);
    }
  }

  private resetData() {
    this.summary = {
      total_alerts: 0,
      high_alerts: 0,
      medium_alerts: 0,
      low_alerts: 0,
    };

    this.categories.forEach((category: string) => {
      this.alertsByCategory[category] = [];
      this.columnsByCategory[category] = this.defaultColumnsByCategory[category] || [];
    });
  }

  private async fetchAlertsSummary() {
    const headers: any = {
      Authorization: localStorage.getItem("t"),
    };

    const url = `${this.buildTellMeUrl("alerts-summary")}&_ts=${Date.now()}`;
    const result: any = await this.apiService.getDataPromis(url, {}, headers);

    if (!result || typeof result != "object") {
      return;
    }

    this.summary = {
      total_alerts: Number(result.total_alerts) || 0,
      high_alerts: Number(result.high_alerts) || 0,
      medium_alerts: Number(result.medium_alerts) || 0,
      low_alerts: Number(result.low_alerts) || 0,
    };
  }

  private async fetchAlertsByCategory(category: string) {
    const headers: any = {
      Authorization: localStorage.getItem("t"),
    };

    const url =
      `${this.buildTellMeUrl("alerts", { category })}` +
      `&_ts=${Date.now()}`;
    const result: any = await this.apiService.getDataPromis(url, {}, headers);

    const records: AzureAlertApiItem[] = Array.isArray(result?.alerts?.[category])
      ? result.alerts[category]
      : [];

    const mappedAlerts = records
      .map((item: AzureAlertApiItem) => this.toViewModel(item))
      .sort((a: AzureAlertViewModel, b: AzureAlertViewModel) => b.eventTimestamp - a.eventTimestamp);
    this.alertsByCategory[category] = mappedAlerts;
    this.columnsByCategory[category] = this.resolveColumnsForCategory(category, mappedAlerts);
  }

  private toViewModel(item: AzureAlertApiItem): AzureAlertViewModel {
    const fromIp: string = this.formatDisplay(item?.from_ip);
    const toIp: string = this.formatDisplay(item?.to_ip);
    const severityBucket: SeverityFilter = this.getSeverityBucket(item?.criticality);
    const eventTimestamp = item?.event_time ? new Date(item.event_time).getTime() : 0;

    return {
      eventTime: this.formatDate(item?.event_time),
      eventTimestamp: isNaN(eventTimestamp) ? 0 : eventTimestamp,
      criticality: this.toTitle(item?.criticality),
      severityBucket,
      description: this.formatDisplay(item?.description),
      identifier: this.formatDisplay(item?.identifier),
      triggeredBy: this.formatDisplay(item?.triggered_by),
      ruleId: this.formatDisplay(item?.rule_id),
      ruleName: this.formatDisplay(item?.rule_name),
      protocol: this.formatDisplay(item?.protocol),
      sourceIp: this.formatDisplay(item?.source_ip),
      fromToIp: fromIp == "-" && toIp == "-" ? "-" : `${fromIp} - ${toIp}`,
    };
  }

  private buildTellMeUrl(path: string, extraParams: Record<string, string> = {}): string {
    const params: string[] = [
      `user_id=${encodeURIComponent(this.userId || "")}`,
      `subscription_id=${encodeURIComponent(this.subscriptionId || "")}`,
    ];

    Object.keys(extraParams).forEach((key: string) => {
      params.push(`${encodeURIComponent(key)}=${encodeURIComponent(extraParams[key])}`);
    });

    return (
      `${environment.apiURL}/${this.urlPrefix}/azure/tellme/api/tellme/${path}` +
      `?${params.join("&")}`
    );
  }

  selectCategory(category: string) {
    if (this.activeCategory == category) {
      return;
    }

    this.activeCategory = category;
    this.currentPage = 1;
  }

  refresh() {
    this.load();
  }

  onSearchInput() {
    this.currentPage = 1;
  }

  toggleSeverityFilter() {
    this.showSeverityFilter = !this.showSeverityFilter;
  }

  setSeverityFilter(filter: SeverityFilter) {
    this.selectedSeverity = filter;
    this.showSeverityFilter = false;
    this.currentPage = 1;
  }

  showHighAlerts() {
    this.setSeverityFilter("high");
  }

  showMediumAlerts() {
    this.setSeverityFilter("medium");
  }

  showLowAlerts() {
    this.setSeverityFilter("low");
  }

  getCategoryCount(category: string): number {
    return (this.alertsByCategory[category] || []).length;
  }

  get currentAlerts(): AzureAlertViewModel[] {
    return this.alertsByCategory[this.activeCategory] || [];
  }

  get currentColumns(): AlertColumn[] {
    const keys = this.columnsByCategory[this.activeCategory] || [];
    return keys.map((key: AlertColumnKey) => ({
      key,
      label: this.columnLabels[key] || key,
    }));
  }

  getCellValue(item: AzureAlertViewModel, key: AlertColumnKey): string {
    return item?.[key] || "-";
  }

  get filteredAlerts(): AzureAlertViewModel[] {
    const keyword = this.searchKeyword.trim().toLowerCase();

    return this.currentAlerts.filter((item: AzureAlertViewModel) => {
      if (this.selectedSeverity != "all" && item.severityBucket != this.selectedSeverity) {
        return false;
      }

      if (!keyword) {
        return true;
      }

      const searchText = [
        item.eventTime,
        item.criticality,
        item.description,
        item.identifier,
        item.triggeredBy,
        item.ruleId,
        item.ruleName,
        item.protocol,
        item.sourceIp,
        item.fromToIp,
      ]
        .join(" ")
        .toLowerCase();

      return searchText.includes(keyword);
    });
  }

  get totalPages(): number {
    const pages = Math.ceil(this.filteredAlerts.length / this.itemsPerPage);
    return pages > 0 ? pages : 1;
  }

  get paginatedAlerts(): AzureAlertViewModel[] {
    const page = Math.min(this.currentPage, this.totalPages);
    const startIndex = (page - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredAlerts.slice(startIndex, endIndex);
  }

  navigateToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  navigateToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  navigateToPage(page: number | "...") {
    if (page === "...") {
      return;
    }

    this.currentPage = page as number;
  }

  getPageNumbers(): (number | "...")[] {
    const totalPageCount = this.totalPages;
    const maxVisiblePages = 5;

    let start = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let end = Math.min(start + maxVisiblePages - 1, totalPageCount);
    start = Math.max(1, end - maxVisiblePages + 1);

    const pageNumbers: (number | "...")[] = [];

    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }

    if (start > 1) {
      pageNumbers.unshift("...");
    }

    if (end < totalPageCount) {
      pageNumbers.push("...");
    }

    return pageNumbers;
  }

  severityClass(item: AzureAlertViewModel): string {
    if (item.severityBucket == "high") {
      return "redg";
    }
    if (item.severityBucket == "medium") {
      return "orangeg";
    }
    if (item.severityBucket == "low") {
      return "greeng";
    }
    return "grayg";
  }

  private ensurePageBounds() {
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
  }

  private getSeverityBucket(criticality: string): SeverityFilter {
    const normalized = (criticality || "").toLowerCase().trim();

    if (normalized == "critical" || normalized == "high") {
      return "high";
    }
    if (normalized == "medium") {
      return "medium";
    }
    if (normalized == "low") {
      return "low";
    }

    return "all";
  }

  private toTitle(value: string): string {
    const normalized = this.formatDisplay(value);
    if (normalized == "-") {
      return "-";
    }
    return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
  }

  private formatDisplay(value: any): string {
    if (value == null) {
      return "-";
    }

    const text = String(value).trim();
    return text == "" ? "-" : text;
  }

  private formatDate(date: string): string {
    if (!date) {
      return "-";
    }

    const parsed = moment(date);
    if (!parsed.isValid()) {
      return this.formatDisplay(date);
    }

    return parsed.format("MMM DD, YYYY hh:mm A");
  }

  private resolveColumnsForCategory(
    category: string,
    alerts: AzureAlertViewModel[]
  ): AlertColumnKey[] {
    const defaults = this.defaultColumnsByCategory[category] || this.defaultColumnsByCategory.Security;

    if (alerts.length == 0) {
      return defaults;
    }

    const columnsWithData = defaults.filter((key: AlertColumnKey) =>
      alerts.some((item: AzureAlertViewModel) => this.getCellValue(item, key) != "-")
    );

    return columnsWithData.length > 0 ? columnsWithData : defaults;
  }
}
